import { Icon } from '@eventbrite/eds-icon';
import { EmptyStar, FullStar, HalfStar } from '@eventbrite/eds-iconography';
import uniqueId from 'lodash/uniqueId';
import React from 'react';
import { useTheme } from '../../../utils/context/theme-context';
import LazyloadImage from '../../sharedComponents/LazyloadImage';
import { BaseModuleProps } from '../types';
import './multipleRatings.scss';

interface IImageBlock {
    imageUrl: string;
    altText: string;
}

export interface IRatingsBlock {
    image: IImageBlock;
    bodyText: string;
    creatorDescription: string;
    creatorName: string;
    ratingScore: number;
}

export interface IMultipleRatings {
    title?: string;
    ratings: IRatingsBlock[];
}

export const getRatingStars = (ratingScore: number): JSX.Element[] => {
    /*
    JIRA EB-195499
    Star decimal values:
        0.0 <-> 0.2 = empty
        0.3 <-> 0.6 = half
        0.7 <-> 0.9 = full
    */

    const starsArray = Array.from({ length: 5 }, (_) => (
        <Icon size={32} type={<FullStar />} key={uniqueId()} />
    ));
    const floorValue = Math.floor(ratingScore);
    let decimalValue = parseInt(
        (ratingScore - floorValue).toFixed(1).split('.')[1],
    );

    for (let i = floorValue; i < starsArray.length; i++) {
        if (decimalValue > 6) {
            starsArray[i] = (
                <Icon size={32} type={<FullStar />} key={uniqueId()} />
            );
        } else if (decimalValue > 2) {
            starsArray[i] = (
                <Icon size={32} type={<HalfStar />} key={uniqueId()} />
            );
        } else {
            starsArray[i] = (
                <Icon size={32} type={<EmptyStar />} key={uniqueId()} />
            );
        }
        decimalValue = 0;
    }
    return starsArray;
};

export const RatingStars = ({
    ratingScore,
}: {
    ratingScore: number;
}): JSX.Element => {
    return (
        <div className={`ratings-container__stars`}>
            <div className="ratings-container__stars__logo">
                {getRatingStars(ratingScore)}
            </div>

            <span className="ratings-container__stars__text">
                {ratingScore}
            </span>
        </div>
    );
};

export const RatingsBlock: React.FC<IRatingsBlock & BaseModuleProps> = ({
    bodyText,
    creatorDescription,
    creatorName,
    ratingScore,
    image: { imageUrl, altText },
    variant,
}) => {
    const baseOptions = { variant };
    const theme = useTheme();
    const [cardClass] = theme.generateStyles('card', baseOptions);
    const [textClass] = theme.generateStyles('card-text', baseOptions);
    return (
        <article className={`ratings-container ${cardClass}`}>
            <RatingStars ratingScore={ratingScore} />
            <div className={`ratings-container__body-text ${textClass}`}>
                {bodyText}
            </div>
            <div className="ratings-container__creator">
                {!!imageUrl && (
                    <LazyloadImage
                        image={imageUrl}
                        alt={altText || 'creator logo'}
                        classes="ratings-container__creator__image"
                        style={{}}
                    />
                )}
                <div
                    className={`ratings-container__creator__content ${textClass}`}
                >
                    <span className="ratings-container__creator__content__name">
                        {creatorName}
                    </span>
                    <span className="ratings-container__creator__content__description">
                        {creatorDescription}
                    </span>
                </div>
            </div>
        </article>
    );
};

const MultipleRatings: React.FC<IMultipleRatings & BaseModuleProps> = ({
    title,
    ratings,
    variant,
}) => {
    const themeOptions = { variant };
    const theme = useTheme();
    const [backgroundClass] = theme.generateStyles('background', themeOptions);
    const [textClass] = theme.generateStyles('text', themeOptions);

    return (
        <section className={`multiple-ratings ${backgroundClass}`}>
            <div
                className={`multiple-ratings__container module-landing-page__module-container-content`}
            >
                <header>
                    <h2
                        className={`multiple-ratings__container__title ${textClass}`}
                    >
                        {title}
                    </h2>
                </header>
                <div className="multiple-ratings__container__content">
                    {ratings.map(
                        ({
                            image,
                            bodyText,
                            creatorDescription,
                            creatorName,
                            ratingScore,
                        }) => (
                            <RatingsBlock
                                image={image}
                                bodyText={bodyText}
                                creatorDescription={creatorDescription}
                                creatorName={creatorName}
                                ratingScore={ratingScore}
                                key={uniqueId()}
                                variant={variant}
                            />
                        ),
                    )}
                </div>
            </div>
        </section>
    );
};

export default MultipleRatings;
